/* eslint-disable import/prefer-default-export */
import { TokenAuthResponsePayload } from '@quality24/feedback-typings';
import jwtDecode from 'jwt-decode';
import { CookieStorage } from './CookieStorage';

const PREFIX = 'Q24IdentityProvider';

interface IdTokenPayload {
  sub: string;
  'custom:user_id'?: string;
  'custom:organization_id': string;
}

/**
 * UserSession class
 */
export class UserSession {
  private clientId: string;

  private lastPlatform: string;

  private userId: string | null = null;

  private idToken: string | null = null;

  private accessToken: string | null = null;

  private userData: Record<string, unknown> | null = null;

  /**
   * Loads session from cookies
   * @param clientId
   */
  constructor(appName: string, clientId: string) {
    this.clientId = clientId;

    const cookiePrefix = `${PREFIX}.${clientId}`;
    this.userId = CookieStorage.getItem(`${cookiePrefix}.LastAuthUser`);
    this.lastPlatform =
      CookieStorage.getItem(`${cookiePrefix}.LastPlatform`) || appName;

    // If platform name does not match, purge session
    if (this.lastPlatform && this.lastPlatform !== appName) {
      UserSession.purge(clientId, this.userId);
      this.lastPlatform = appName;
      this.userId = null;
    }

    if (typeof this.userId === 'string' && this.userId) {
      const userPrefix = `${cookiePrefix}.${this.userId}`;
      this.idToken = CookieStorage.getItem(`${userPrefix}.idToken`);
      this.accessToken = CookieStorage.getItem(`${userPrefix}.accessToken`);
      this.userData = JSON.parse(
        CookieStorage.getItem(`${userPrefix}.userData`),
      );
    }
  }

  /**
   * Checks if credentials are valid (not expired)
   * @returns
   */
  public isValid() {
    if (!this.accessToken) return false;

    // Decode token
    try {
      const decoded = jwtDecode<{ exp: number }>(this.accessToken);

      // Check token expiration
      return decoded.exp > Date.now() / 1000;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('UserSession: Failed to decode access token');
      return false;
    }
  }

  /**
   * Checks if user is authenticated
   * @returns
   */
  public authenticated() {
    return !!this.accessToken && this.isValid();
  }

  /**
   * (static) Purges the session from storage
   */
  static purge(clientId: string, userId: string | null) {
    const cookiePrefix = `${PREFIX}.${clientId}`;
    CookieStorage.removeItem(`${cookiePrefix}.LastAuthUser`);
    CookieStorage.removeItem(`${cookiePrefix}.LastPlatform`);
    CookieStorage.removeItem(`${cookiePrefix}.${userId}.idToken`);
    CookieStorage.removeItem(`${cookiePrefix}.${userId}.accessToken`);
    CookieStorage.removeItem(`${cookiePrefix}.${userId}.userData`);
  }

  /**
   * Purges the session from storage and memory. The in-memory credentials are
   * what `Authenticator.currentSession()` hands the API clients, so leaving
   * them behind keeps the signed-out session's token on every request until
   * the next `initSession`.
   */
  public purge() {
    UserSession.purge(this.clientId, this.userId);
    this.userId = null;
    this.idToken = null;
    this.accessToken = null;
    this.userData = null;
  }

  /**
   * Initiates a new session, sets JWT into memory and cookie storage
   * @param payload
   */
  public initSession(payload: TokenAuthResponsePayload) {
    if (!payload.idToken) throw new Error('Auth payload has no ID Token');

    // Set session values
    const decoded = jwtDecode<IdTokenPayload>(payload.idToken);
    this.userId = decoded['custom:user_id'] ?? decoded.sub;
    this.idToken = payload.idToken;
    this.accessToken = payload.accessToken;
    this.userData = {
      ...payload.userData,
      organizationId: decoded['custom:organization_id'],
      userId: this.userId,
    };

    // Set storage
    const cookiePrefix = `${PREFIX}.${this.clientId}`;
    CookieStorage.setItem(`${cookiePrefix}.LastAuthUser`, this.userId);
    CookieStorage.setItem(`${cookiePrefix}.LastPlatform`, this.lastPlatform);
    CookieStorage.setItem(
      `${cookiePrefix}.${this.userId}.idToken`,
      this.idToken,
    );
    CookieStorage.setItem(
      `${cookiePrefix}.${this.userId}.accessToken`,
      this.accessToken,
    );
    CookieStorage.setItem(
      `${cookiePrefix}.${this.userId}.userData`,
      JSON.stringify(this.userData),
    );
  }

  public getIdToken() {
    return this.idToken;
  }

  public getAccessToken() {
    return this.accessToken;
  }

  public getUserData() {
    return this.userData;
  }

  public getUserId() {
    return this.userId;
  }
}
